import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { fromJS } from 'immutable';
import { useCallback } from 'react';
import { RANGE_TYPES } from 'reporting-data/constants/dateRangeTypes';
import { BETWEEN } from 'reporting-data/constants/operators';
import { getDateRangeFilterProperty, getDisplayParams, getDisplayParamsSubAppKey, getReportConfig } from 'reporting-data/report/configReportGetters';
import { getCustomWidgetType } from 'reporting-data/report/reportGetters';
import { SUB_APPS } from 'reporting-data/tsTypes/subAppTypes';
import { getUseFiscalYear } from 'reporting-data/lib/fiscalYearUtils';
// @ts-expect-error Untyped dependency
import DateRangeLabel from 'reporting-ui-components/components/shared/DateRangeLabel';
// @ts-expect-error Untyped dependency
import DateRangePicker from 'reporting-ui-components/components/shared/DateRangePicker';
import { DateActionTypes } from 'reporting-ui-components/constants/trackingConstants';
import { EVENTS } from 'reporting-ui-components/lib/events';
import useCloneTracker from 'reporting-ui-components/lib/useCloneTracker';
import BasePopoverEditorComponent from './common/BasePopoverEditorComponent';
import { hasSecondaryDateRangeEditor } from './common/dateRangeHelpers';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SUB_APP_SECONDARY_DATE_PROPERTY_MAP = {
  [SUB_APPS.changeHistoryDealList]: 'closedate'
};
const getSecondaryDateRangeValue = report => {
  const range = report.getIn(['displayParams', 'salesAnalytics', 'context', 'secondaryDateRange']) || fromJS({
    rangeType: RANGE_TYPES.ALL
  });
  return range;
};
const getSecondaryDateRangeProperty = report => {
  const reportConfig = getReportConfig(report);
  const primaryDateRangeProperty = getDateRangeFilterProperty(reportConfig);
  const subAppKey = getDisplayParamsSubAppKey(report);
  if (!subAppKey) {
    return primaryDateRangeProperty;
  }
  const secondaryDateRangeProperty = SUB_APP_SECONDARY_DATE_PROPERTY_MAP[subAppKey];
  return secondaryDateRangeProperty || primaryDateRangeProperty;
};
const SecondaryDateRangeEditor = ({
  appUsageTracker,
  isV2FilterExperience,
  onChange,
  report,
  trackingApplicationName,
  trackingAssetType,
  trackingScreenName
}) => {
  const usageTracker = useCloneTracker(appUsageTracker);
  const secondaryDateRange = getSecondaryDateRangeValue(report);
  const reportConfig = getReportConfig(report);
  const handleChange = useCallback(dateRangeValue => {
    const dateRangeProperty = getSecondaryDateRangeProperty(report);
    usageTracker.track(EVENTS.changeSrvDateFilters, {
      action: DateActionTypes.SECONDARY_DATE_RANGE,
      assetType: trackingAssetType || 'report',
      applicationName: trackingApplicationName,
      whereScreen: trackingScreenName,
      isNewFilterExperience: isV2FilterExperience
    });
    const {
      startDate,
      endDate
    } = dateRangeValue;
    const newDateFilters = [{
      operator: BETWEEN,
      property: dateRangeProperty,
      value: startDate,
      highValue: endDate,
      dateTimeFormat: 'DATE'
    }];
    const updatedReport = report.setIn(['displayParams', 'salesAnalytics', 'context', 'secondaryDateRange'], fromJS(dateRangeValue)).updateIn(['config', 'filters', 'custom'], customFilters => {
      const unaffectedFilters = customFilters.filter(filter => filter.get('property') !== dateRangeProperty).toJS();
      return fromJS(unaffectedFilters.concat(newDateFilters));
    });
    onChange(updatedReport);
  }, [isV2FilterExperience, onChange, report, trackingApplicationName, trackingAssetType, trackingScreenName, usageTracker]);
  const label = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "reporting-enablement.reportEditors.secondaryDateRangeEditor.label"
  });
  const dateRangeInput = /*#__PURE__*/_jsx(DateRangePicker, Object.assign({
    buttonUse: "form",
    condensed: true,
    dataTestId: "secondary-date-range-filter-date-range-picker"
  }, secondaryDateRange.toJS(), {
    useFiscalYear: false,
    isFiscalYearEnabled: false,
    onChange: handleChange
  }));
  if (isV2FilterExperience) {
    const buttonContent = /*#__PURE__*/_jsx(DateRangeLabel, {
      dateRange: getSecondaryDateRangeValue(report),
      useFiscalYear: getUseFiscalYear(reportConfig)
    });
    const toolTipContent = () => {
      return /*#__PURE__*/_jsxs("div", {
        children: [label, /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx("b", {
          children: buttonContent
        })]
      });
    };
    const popoverBody = /*#__PURE__*/_jsx(View, {
      inlineSize: "300px",
      paddingBlockEnd: "500",
      children: dateRangeInput
    });
    return /*#__PURE__*/_jsx(BasePopoverEditorComponent, {
      buttonContent: buttonContent,
      dataTestId: "open-secondary-date-range-filter-button",
      hasSelectedValue: true,
      tooltipContent: toolTipContent(),
      popoverBody: popoverBody
    });
  }
  return /*#__PURE__*/_jsx(FormControl, {
    label: label,
    children: dateRangeInput
  });
};
SecondaryDateRangeEditor.isUsableForReport = report => {
  const customWidgetType = getCustomWidgetType(report);
  const displayParams = getDisplayParams(report);
  return hasSecondaryDateRangeEditor(customWidgetType, displayParams);
};
SecondaryDateRangeEditor.isDateEditor = true;
SecondaryDateRangeEditor.isFilterEditor = true;
export default SecondaryDateRangeEditor;